import type { FormInstance } from '@arco-design/web-react';

import { type VendorDeviceCompositionItem, type VendorDeviceSelection, formatVendorDeviceLabel } from '@/delivery-type-plugin/components';
import { buildOptionFromRaw, syncOptionMetaFromRaw } from '@/delivery-type-plugin/platform/option-meta';

import { EBM_RESOURCE_CONFIG_OPTION_META_KEYS } from '../fieldKeys';
import type { EbmResourceConfigPaths, EbmResourceConfigurationRecord } from '../types';

/** 计费项 detail 解析结果（数组首项） */
export interface BillDeviceDetailItem {
  architecture?: string;
  vendor?: string;
  device?: string;
  deviceID?: string;
  vendorID?: string;
  /** 库存节点名（展示/预览用；完整性由后端查 resource-configurations 补齐） */
  nodes?: string[];
  composition?: VendorDeviceCompositionItem[];
}

/** 解析 VendorAndDeviceRadioList 返回的 detail JSON */
export function parseBillDeviceDetail(detail?: string): BillDeviceDetailItem | null {
  if (!detail) return null;
  try {
    const parsed = JSON.parse(detail) as unknown;
    return Array.isArray(parsed) ? (parsed[0] as BillDeviceDetailItem) : null;
  } catch {
    return null;
  }
}

/** 将 composition 数量格式化为 resource.size 字符串 */
function formatCompositionSize(item: VendorDeviceCompositionItem): string | undefined {
  const total = item.quantity?.total;
  if (total == null) return undefined;
  const text = String(total).trim();
  if (!text) return undefined;
  /** 内存容量：单位取 composition.quantity.unit.default（如 Mi/Gi），缺省回退 Gi */
  if (item.code === 'Memory') {
    if (/[a-zA-Z]+$/.test(text)) return text;
    const unit = String(item.quantity?.unit?.default || '').trim() || 'Gi';
    return `${text}${unit}`;
  }
  return text;
}

/** NVMe composition：capacity 原样落表（对象/标量均保持，提交勿格式化为字符串） */
function readNvmeCapacityFromComposition(item: VendorDeviceCompositionItem): unknown {
  if (item.code !== 'NVMe') return undefined;
  const extraInfo = item.extraInfo;
  if (!extraInfo || typeof extraInfo !== 'object' || Array.isArray(extraInfo)) return undefined;
  const capacity = (extraInfo as Record<string, unknown>).capacity;
  if (capacity == null || capacity === '') return undefined;
  if (typeof capacity === 'object' && !Array.isArray(capacity) && Object.keys(capacity as object).length === 0) {
    return undefined;
  }
  return capacity;
}

/** 单条 composition → 硬件对象（对齐 resource-configurations / 直建提交字段） */
function mapCompositionItemToHardware(item: VendorDeviceCompositionItem): Record<string, unknown> {
  const size = formatCompositionSize(item);
  const base: Record<string, unknown> = {
    vendor: item.vendor,
    device: item.device || item.name,
  };
  const vendorID = String(item.vendorID || '').trim();
  const deviceID = String(item.deviceID || '').trim();
  if (vendorID) base.vendorID = vendorID;
  if (deviceID) base.deviceID = deviceID;
  if (item.code === 'Memory') {
    base.deviceType = item.resourceType || item.device || item.name;
  }
  const nvmeCapacity = readNvmeCapacityFromComposition(item);
  if (nvmeCapacity !== undefined) {
    base.capacity = nvmeCapacity;
  }
  if (size != null) {
    /**
     * resource.name：优先 quantity.resourceName（计费 detail 权威值）；
     * 缺省时 NVMe 回退 disk（与直建一致），其余用 code 小写。
     */
    const fromQuantity = String(item.quantity?.resourceName || '').trim();
    const resourceName = fromQuantity || (item.code === 'NVMe' ? 'disk' : String(item.code || '').toLowerCase() || 'resource');
    base.resource = { name: resourceName, size };
  }
  return base;
}

/** composition → 非计费路径使用的 resourceConfiguration 结构 */
export function buildResourceConfigurationFromComposition(
  composition: VendorDeviceCompositionItem[] | undefined,
): EbmResourceConfigurationRecord {
  const list = Array.isArray(composition) ? composition : [];
  const cpuItem = list.find((item) => item.code === 'CPU');
  const memoryItem = list.find((item) => item.code === 'Memory');
  const gpuItem = list.find((item) => item.code === 'GPU');
  const storageItems = list.filter((item) => item.code === 'NVMe' || item.code === 'Storage' || item.code === 'Disk');
  const nicItems = list.filter((item) => item.code === 'NIC');

  return {
    key: '',
    nodes: [],
    cpu: cpuItem ? mapCompositionItemToHardware(cpuItem) : {},
    memory: memoryItem ? mapCompositionItemToHardware(memoryItem) : {},
    gpu: gpuItem ? mapCompositionItemToHardware(gpuItem) : {},
    storage: storageItems.map(mapCompositionItemToHardware),
    nic: nicItems.map(mapCompositionItemToHardware),
  };
}

/** 组装计价模式 optionMeta.ebmNode 记录（选中态恢复 + 计价投影） */
export function buildBillingEbmNodeOptionRecord(
  selection: VendorDeviceSelection,
  detailItem: BillDeviceDetailItem | null,
): Record<string, unknown> {
  const nodeKey = selection.billItemId || EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode;
  const displayLabel = formatVendorDeviceLabel({ vendor: selection.vendor, device: selection.device });
  return {
    value: nodeKey,
    name: displayLabel,
    label: displayLabel,
    vendor: selection.vendor,
    device: selection.device,
    vendorID: selection.vendorID,
    deviceID: selection.deviceID,
    deviceType: selection.deviceType,
    billItemId: selection.billItemId,
    architecture: detailItem?.architecture,
    detail: selection.detail,
    composition: detailItem?.composition,
    /** 会话内保留，供列表回填匹配；不进入提交 API */
    useSubResourcePricing: Boolean(selection.useSubResourcePricing),
    ...(selection.quantityUnit ? { quantityUnit: selection.quantityUnit } : {}),
  };
}

/**
 * 计价选择：按计费项 composition（含 vendorID/deviceID 等）写入 resourceConfiguration；
 * 完整性由后端自行查 resource-configurations 保障。同步 optionMeta.ebmNode 供回填与计价。
 */
export function applyBillingResourceSelection(form: FormInstance, paths: EbmResourceConfigPaths, selection: VendorDeviceSelection): void {
  const detailItem = parseBillDeviceDetail(selection.detail);
  const serialized = JSON.stringify(buildResourceConfigurationFromComposition(detailItem?.composition));

  form.setFieldValue(paths.resourceConfiguration, serialized);

  const nodeKey = selection.billItemId || EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode;
  const built = buildOptionFromRaw(nodeKey, buildBillingEbmNodeOptionRecord(selection, detailItem));
  syncOptionMetaFromRaw(form, nodeKey, built, EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode);
}

/** 计价模式：清除 resourceConfiguration 与 optionMeta */
export function clearBillingResourceSelection(form: FormInstance, paths: EbmResourceConfigPaths): void {
  form.setFieldValue(paths.resourceConfiguration, undefined);
  syncOptionMetaFromRaw(form, undefined, undefined, EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode);
}
